import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/modern/components/ui/dialog';
import { Button } from '@/modern/components/ui/button';
import { X, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';

export interface PlanningEventDetails {
  label: string;
  date: string;
  color: string;
  startDate: string;
  endDate: string;
  duration: string;
}

interface PlanningDetailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  planningEvents: PlanningEventDetails[];
}

const PlanningDetailModal: React.FC<PlanningDetailModalProps> = ({
  open,
  onOpenChange,
  planningEvents
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[400px] max-h-[85vh] flex flex-col p-0 shadow-none">
        <DialogHeader className="px-6 pt-6 pb-2 sticky top-0 bg-background z-10 border-0">
          <div className="flex items-center justify-between gap-2">
            <DialogTitle className="text-base font-normal break-words min-w-0 flex-1">
              {planningEvents.length === 1 ? planningEvents[0].label : `Planning Events (${planningEvents.length})`}
            </DialogTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-8 w-8 p-0 hover:bg-muted shrink-0"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </DialogHeader>

        <div className="overflow-y-auto px-6 pt-2 pb-6 space-y-4">
          {planningEvents.length === 1 ? (
            // Single planning event - no card wrapper
            <div className="space-y-2">
              <div className="border-t border-border pt-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-foreground">
                    {planningEvents[0].startDate && planningEvents[0].endDate ?
                      `${format(new Date(planningEvents[0].startDate), 'EEE M-d')} → ${format(new Date(planningEvents[0].endDate), 'EEE M-d')}` :
                      'Date range not available'
                    }
                  </span>
                </div>

                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-muted-foreground">{planningEvents[0].duration}</span>
                </div>
              </div>
            </div>
          ) : (
            // Multiple planning events - keep card structure
            planningEvents.map((event, index) => (
              <div
                key={index}
                className="border border-border rounded-lg p-4 bg-card"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 min-w-0">
                      <h3 className="text-base font-semibold text-foreground break-words min-w-0 flex-1">
                        {event.label}
                      </h3>
                      <div className="flex items-center gap-1 shrink-0">
                        <div
                          className="w-3 h-3 rounded-full border border-border"
                          style={{ backgroundColor: event.color }}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="border-t border-border pt-2 mt-2 space-y-2">
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-foreground">
                            {event.startDate && event.endDate ?
                              `${format(new Date(event.startDate), 'EEE M-d')} → ${format(new Date(event.endDate), 'EEE M-d')}` :
                              'Date range not available'
                            }
                          </span>
                        </div>

                        <div className="flex items-center gap-2 text-sm">
                          <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-muted-foreground">{event.duration}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}

          {planningEvents.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No planning events found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PlanningDetailModal;
